import camelCase from 'lodash/camelCase';
import snakeCase from 'lodash/snakeCase';

import { gql } from '@zenbusiness/application-commons-graphql';

const PROMPT_BASE_FIELDS = gql(`
  fragment PromptBaseFields on Prompt {
    uuid
    systemContent
    userContent
    name
    description
    version
    status
    maxCompletionTokens
    temperature
    model
  }
`);

const COMPLETIONS_BASE_FIELDS = gql(`
  fragment CompletionsBaseFields on Completion {
    id
    model
    messages {
      role
      content
    }
  }
`);

export const SMART_ASSIST_PROMPT_QUERY = gql(`
  query FindPrompt($input: FindPromptInput!) {
    findPrompt(input: $input) {
      ...PromptBaseFields
      structuredOutput {
        name
        schema {
          ... on SmartAssistStructuredOutputSchema{
            reasons
            introduction
            conclusion
          }
        }
      }
    }
  }
  ${PROMPT_BASE_FIELDS}
`);

export const ARTICLE_PROMPT_QUERY = gql(`
  query FindArticlePrompt($input: FindPromptInput!) {
    findPrompt(input: $input) {
      ...PromptBaseFields
      structuredOutput {
        name
        schema {
          ... on ArticleStructuredOutputSchema{
            article
          }
        }
      }
    }
  }
  ${PROMPT_BASE_FIELDS}
`);

export const BUSINESS_PROFILE_PROMPT_QUERY = gql(`
  query FindBusinessProfilePrompt($input: FindPromptInput!) {
    findPrompt(input: $input) {
      ...PromptBaseFields
      structuredOutput {
        name
        schema {
          ... on BusinessProfileStructuredOutputSchema{
            output
            matchedReason
            matchedResult
          }
        }
      }
    }
  }
  ${PROMPT_BASE_FIELDS}
`);

export const SMART_ASSIST_COMPLETIONS_MUTATION = gql`
  mutation Completions(
    $model: String!,
    $temperature: Float!,
    $maxCompletionTokens: Int!,
    $metadata: PromptMetadata!
    $messages: [CompletionMessageInput!]!
    $responseFormat: CompletionResponseFormatInput!
  ) {
    completions(
      model: $model,
      temperature: $temperature,
      maxCompletionTokens: $maxCompletionTokens,
      metadata: $metadata,
      messages: $messages,
      responseFormat: $responseFormat
    ) {
      ...CompletionsBaseFields
      messages {
        parsed {
          ... on SmartAssistCompletionParsedMessage{
            conclusion
            introduction
            reasons {
              title
              description
            }
          }
        }
      }
    }
  }
  ${COMPLETIONS_BASE_FIELDS}
`;

export const ARTICLE_COMPLETIONS_MUTATION = gql`
  mutation ArticleCompletions(
    $model: String!,
    $temperature: Float!,
    $maxCompletionTokens: Int!,
    $metadata: PromptMetadata!
    $messages: [CompletionMessageInput!]!
    $responseFormat: CompletionResponseFormatInput!
  ) {
    completions(
      model: $model,
      temperature: $temperature,
      maxCompletionTokens: $maxCompletionTokens,
      metadata: $metadata,
      messages: $messages,
      responseFormat: $responseFormat
    ) {
      ...CompletionsBaseFields
      messages {
        parsed {
          ... on ArticleCompletionParsedMessage{
            article
          }
        }
      }
    }
  }
  ${COMPLETIONS_BASE_FIELDS}
`;

export const BUSINESS_PROFILE_COMPLETIONS_MUTATION = gql`
  mutation BusinessProfileCompletions(
    $model: String!,
    $temperature: Float!,
    $maxCompletionTokens: Int!,
    $metadata: PromptMetadata!
    $messages: [CompletionMessageInput!]!
    $responseFormat: CompletionResponseFormatInput!
  ) {
    completions(
      model: $model,
      temperature: $temperature,
      maxCompletionTokens: $maxCompletionTokens,
      metadata: $metadata,
      messages: $messages,
      responseFormat: $responseFormat
    ) {
      ...CompletionsBaseFields
      messages {
        parsed {
           ... on BusinessProfileCompletionParsedMessage{
            output
            matchedReason
            matchedResult
          }
        }
      }
    }
  }
  ${COMPLETIONS_BASE_FIELDS}
`;

export const PROMPT_QUERY_MAP = {
  smartAssist: SMART_ASSIST_PROMPT_QUERY,
  businessProfile: BUSINESS_PROFILE_PROMPT_QUERY,
  article: ARTICLE_PROMPT_QUERY
};

export const COMPLETIONS_MUTATION_MAP = {
  smartAssist: SMART_ASSIST_COMPLETIONS_MUTATION,
  businessProfile: BUSINESS_PROFILE_COMPLETIONS_MUTATION,
  article: ARTICLE_COMPLETIONS_MUTATION
};

/**
 * Replaces placeholders in a string with values.
 * Adds key names if data doesn't contain ':'.
 *
 * @param {object} values - Key-value pairs to fill into the template.
 * @param {string} data - String containing {{placeholders}}.
 * @returns {string} - Resulting string with values filled in.
 */
export const fillContent = (values, data = '') => {
  let filledString = data;
  const keysToFill = Object.keys(values);

  const includeKeyNames = !data.includes(':');

  keysToFill.forEach((key) => {
    const snakeKey = snakeCase(key);
    const camelKey = camelCase(key);
    const value = values[key];

    const snakeReplace = includeKeyNames ? `${snakeKey}: ${value}` : `${value}`;
    const camelReplace = includeKeyNames ? `${camelKey}: ${value}` : `${value}`;

    filledString = filledString.replaceAll(`{{${snakeKey}}}`, snakeReplace);
    filledString = filledString.replaceAll(`{{${camelKey}}}`, camelReplace);
  });

  return filledString;
};
